
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import LegalFooter from '@/components/LegalFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error('Lien de réinitialisation invalide ou incomplet');
      return;
    }
    if (password.length < 6) {
      toast.error('Le mot de passe doit comporter au moins 6 caractères');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Les deux mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      if (response.ok) {
        setDone(true);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de la réinitialisation');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#0a1628] via-[#1a365d] to-[#2563eb]">
      <div className="w-full max-w-[420px]">
        <div
          className="bg-white rounded-2xl overflow-hidden p-8"
          style={{ boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.35), 0 10px 30px -10px rgba(0, 0, 0, 0.2), 0 0 40px -5px rgba(59, 130, 246, 0.15)' }}
        >
          {done ? (
            <div className="text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-[#dcfce7] flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-7 w-7 text-[#16a34a]" />
              </div>
              <h1 className="text-[#0f172a] text-xl font-semibold">Mot de passe réinitialisé</h1>
              <p className="text-sm text-[#64748b]">
                Votre nouveau mot de passe est actif. Vous pouvez maintenant vous connecter.
              </p>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full mt-2 py-3 bg-[#2563eb] text-white text-sm font-semibold rounded-xl hover:bg-[#1d4ed8] transition-colors"
              >
                Retour à la connexion
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <img
                  src="/logo-action-securite.png"
                  alt="Action Sécurité"
                  className="h-20 mx-auto mb-4 rounded-lg"
                />
                <h1 className="text-[#0f172a] text-xl font-semibold tracking-tight mb-1">
                  Nouveau mot de passe
                </h1>
                <p className="text-[#9ca3af] text-sm">
                  Choisissez un nouveau mot de passe pour votre compte
                </p>
              </div>

              {!token && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                  Ce lien est invalide ou incomplet. Merci de refaire une demande de réinitialisation
                  depuis la page de connexion.
                </p>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-[#374151]">Nouveau mot de passe</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <Lock className="h-[18px] w-[18px] text-[#9ca3af]" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-11 pr-11 py-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-sm text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:border-[#2563eb] focus:ring-3 focus:ring-[#2563eb]/15 transition-all duration-150"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-[#9ca3af] hover:text-[#64748b] transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-[#374151]">Confirmer le mot de passe</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <Lock className="h-[18px] w-[18px] text-[#9ca3af]" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-11 pr-4 py-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-sm text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:border-[#2563eb] focus:ring-3 focus:ring-[#2563eb]/15 transition-all duration-150"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !token}
                  className="w-full mt-2 py-3 bg-[#2563eb] text-white text-sm font-semibold rounded-xl transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed hover:bg-[#1d4ed8]"
                >
                  {loading ? 'Réinitialisation...' : 'Réinitialiser mon mot de passe'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      <LegalFooter className="absolute bottom-3 inset-x-0 !mt-0 text-blue-200/70" />
    </div>
  );
};

export default ResetPassword;
